import VModalFunc from '@/components/vkyc/v-modal-function';
import { VAction, VMessageType, VStatus } from '@/interfaces/event-log';
import { getTransDetail } from 'components/common/detail-modal';
import i18n from 'i18n';
import {
  AgentLeaveType,
  ClientCommand,
  ClientLeaveType,
  ServerToClientCommand,
  WSCode,
} from 'interfaces/socket';
import { IStats } from 'interfaces/statistics';
import vHistory, { VPathKey } from 'lib/history';
import moment from 'moment';
import { sendOTSentry } from 'sentry';
import clientStore from 'store/client-store';
import socketStore from 'store/socket';
import statsStore from 'store/statistics';
import { throttle } from 'throttle-debounce';
import { isDevServer } from 'util/common';
import { eventTrack } from './event-track';

const VERSION = 'v1';
const RETRY_TIMES = 3;
const HEARTBEAT_DURATION = 5000;

class VWebSocket {
  private timer?: any;
  // private networkLogTimer?: any;
  private uri = '';
  private ws: WebSocket | null = null;
  private userConnected = false; // client connected
  private wsConnected = false; // has ws connected
  private retryTimes = RETRY_TIMES;
  private retryTimesConnect = RETRY_TIMES;
  private hasShowedTip = false;
  private showExpiredTokenNoti = throttle(1500, false, () => {
    this.directToCompleteDependOnVideoCallStart();
  });

  public initAndOpen() {
    // if agent didn't joined, we need the access token!! thinking...
    if (!this.userConnected && clientStore.getAccessToken()) {
      try {
        this.uri = this.getWebSocketUri() || '';
        console.log('ws uri: ', this.uri);
        this.open();
      } catch (e) {
        console.log('open ws error', e);
      }
    } else if (!clientStore.getAccessToken()) {
      this.directToComplete();
    }
  }

  public directToComplete() {
    if (vHistory.currentPathIs(VPathKey.WAITING_ROOM)) {
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    } else if (vHistory.currentPathIs(VPathKey.CHATTING_ROOM)) {
      socketStore.setStatusToThankYou();
      socketStore.goToComplete();
    }
  }

  public directToCompleteDependOnVideoCallStart() {
    if (vHistory.currentPathIs(VPathKey.WAITING_ROOM)) {
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    } else if (
      vHistory.currentPathIs(VPathKey.CHATTING_ROOM) &&
      socketStore.getSocketData().videoCallStart
    ) {
      socketStore.setStatusToThankYou();
      socketStore.goToComplete();
    } else if (
      vHistory.currentPathIs(VPathKey.CHATTING_ROOM) &&
      !socketStore.getSocketData().videoCallStart
    ) {
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
    }
  }

  public close() {
    if (!this.ws) return;
    this.ws.close();
  }

  public async clientSendVendorErrorLeave(detail: any) {
    if (detail.name === 'OT_NOT_CONNECTED') {
      VModalFunc({
        type: 'error',
        title: 'ERROR',
        content: i18n.t(`opentok.error.OT_NOT_CONNECTED`),
        destroyOthers: true,
      });
    } else {
      eventTrack.pushEvent({
        action: ClientCommand.VENDOR_ERROR,
        extra: {
          isAgent: false,
          detail: detail,
          userAgent: navigator.userAgent,
        },
      });
      await eventTrack.flushEvents();
      this.send(ClientCommand.VENDOR_ERROR, {
        isAgent: false,
        detail: detail,
        userAgent: navigator.userAgent,
      });
      sendOTSentry(detail);
      socketStore.setStatusToSorry();
      socketStore.goToComplete();
      VModalFunc({
        type: 'error',
        title: 'ERROR',
        content: i18n.t(`opentok.error.${detail.name}`, i18n.t('opentok.message.Others')),
        destroyOthers: true,
        details: getTransDetail(detail),
      });
    }
  }

  public replyAssignAgentIfSuccess(ifSuccess: boolean) {
    // if users refresh the browser, then we won't send this command again
    if (!socketStore.getSocketData().clientReady) {
      this.replyIfSuccess(ClientCommand.REPLY_ASSIGN_AGENT, ifSuccess);
    }
    if (ifSuccess && socketStore.getSocketData().videoCallStart) {
      statsStore.initSendStatsTimer();
    }
  }

  public sendJoin() {
    this.send(ClientCommand.JOIN, null);
  }

  public sendConnect() {
    this.send(ClientCommand.CONNECT, {
      accessToken: clientStore.getAccessToken(),
    });
  }

  public sendStats(stats: IStats | null) {
    if (!stats) return;
    this.send(ClientCommand.STATISTICS, {
      detail: stats,
    });
  }

  private getWebSocketUri() {
    if (isDevServer() && !process.env.WS_ENDPOINT) {
      throw Error('Please set the ws uri');
    }
    return isDevServer() ? process.env.WS_ENDPOINT : `wss://${location.host}/webSocket`;
  }

  private initTimer() {
    console.log('init ping timer');
    this.timer = window.setTimeout(() => {
      this.send(ClientCommand.PING, null);
      this.initTimer();
    }, HEARTBEAT_DURATION);
  }

  private replyIfSuccess(command: ClientCommand, ifSuccess: boolean) {
    const data = {
      success: ifSuccess,
    };
    this.send(command, data);
  }

  // give the tip to client that agent leave and return to the top of the queue
  private showAgentPauseModal = () => {
    // only allow to show one modal
    if (!this.hasShowedTip) {
      this.hasShowedTip = true;
      VModalFunc({
        type: 'warning',
        title: i18n.t('common.SORRY'),
        content: i18n.t('message.agentPause'),
        onOk: () => {
          eventTrack.pushEvent({
            action: VAction.AGENT_ISSUE_MODAL_CLICK,
          });
          socketStore.updateVideoCallStart(false);
          webSocket.sendJoin();
          this.hasShowedTip = false;
          vHistory.toPageByKey(VPathKey.WAITING_ROOM);
        },
      });
    }
  };

  private open() {
    if (!this.wsConnected || !this.ws) {
      this.ws = new WebSocket(this.uri);
    }
    if (!this.ws) return;
    // when connect
    this.ws.onopen = () => {
      console.log('web socket open');
      this.initTimer();
      this.wsConnected = true;
      this.sendConnect();
    };
    // when receive msg from server
    // eslint-disable-next-line complexity
    this.ws.onmessage = (event: WebSocketMessageEvent) => {
      try {
        if (event?.data) {
          const dataObj = JSON.parse(event.data);
          const { command, version, data, code } = dataObj;
          if (command !== ServerToClientCommand.PONG) {
            eventTrack.pushEvent({
              action: command,
              status: {
                status: code === WSCode.SUCCESS ? VStatus.SUCCESS : VStatus.FAILURE,
                errorCode: code === WSCode.SUCCESS ? undefined : code,
                type: VMessageType.WEBSOCKET,
              },
              extra: code === WSCode.SUCCESS ? undefined : data,
            });
          }
          console.log('ws receive: ', command, moment().format('HH:mm:ss'));
          if (version !== VERSION) {
            return;
          }
          if (code === WSCode.EXPIRED_TOKEN) {
            clearTimeout(this.timer);
            statsStore.clearSendStatsTimerIfSendNow(false);
            this.showExpiredTokenNoti();
            return;
          }
          switch (command) {
            case ServerToClientCommand.FATAL_ERROR:
              socketStore.setStatusToSorry();
              socketStore.goToComplete();
              VModalFunc({
                type: 'error',
                title: i18n.t('common.SORRY'),
                content: i18n.t(`message.serviceError`),
                destroyOthers: true,
              });
              break;
            case ServerToClientCommand.REPLY_CONNECT:
              if (data.success === true) {
                this.retryTimesConnect = RETRY_TIMES;
                this.userConnected = true;
                if (!socketStore.getSocketData().userConnectedBefore) {
                  this.sendJoin();
                  socketStore.updateUserConnectedBefore(true);
                }
              } else {
                if (this.retryTimesConnect <= 0) {
                  this.retryTimesConnect = RETRY_TIMES;
                  this.userConnected = false;
                  socketStore.setStatusToSorry();
                  socketStore.goToComplete();
                } else {
                  this.retryTimesConnect--;
                  this.sendConnect();
                }
              }
              break;
            case ServerToClientCommand.REPLY_JOIN:
              if (data.success === true) {
                this.retryTimes = RETRY_TIMES;
              } else {
                if (this.retryTimes <= 0) {
                  this.retryTimes = RETRY_TIMES;
                  socketStore.setStatusToSorry();
                  socketStore.goToComplete();
                } else {
                  this.retryTimes--;
                  this.sendJoin();
                }
              }
              break;
            case ServerToClientCommand.WAITING_NO:
              if (data.waitingNo !== null) {
                socketStore.updateWaitingNumber(data.waitingNo);
              }
              break;
            case ServerToClientCommand.ASSIGN_AGENT:
              if (data) {
                socketStore.updateVideoCallInfo(data);
                vHistory.toPageByKey(VPathKey.CHATTING_ROOM);
              } else {
                console.log('assign agent error');
              }
              break;
            case ServerToClientCommand.CONFIRM_REPLY_ASSIGN_AGENT:
              if (data?.success === true) {
                socketStore.updateClientReady(true);
              } else {
                socketStore.updateClientReady(false);
                if (code === WSCode.AGENT_INFO_STATE_ERROR) {
                  // this.showAgentPauseModal();
                } else {
                  socketStore.setStatusToSorry();
                  socketStore.goToComplete();
                }
              }
              break;
            case ServerToClientCommand.VIDEO_CALL_START:
              socketStore.updateVideoCallStart(true);
              statsStore.initSendStatsTimer();
              break;
            case ServerToClientCommand.NOTICE_LEAVE:
              statsStore.clearBothTimer();
              statsStore.clearSendStatsTimerIfSendNow(true);
              webSocket.replyIfSuccess(ClientCommand.REPLY_NOTICE_LEAVE, true);
              // if agent submit / cancel
              if (
                [
                  AgentLeaveType.AGENT_CANCEL,
                  AgentLeaveType.AGENT_SUBMIT,
                  ClientLeaveType.CLIENT_REPLY_TIMEOUT,
                ].includes(data?.type)
              ) {
                if (
                  [AgentLeaveType.AGENT_CANCEL, ClientLeaveType.CLIENT_REPLY_TIMEOUT].includes(
                    data.type,
                  )
                ) {
                  socketStore.setStatusToSorry();
                } else {
                  socketStore.setStatusToThankYou();
                }
                socketStore.goToComplete();
              } else if (
                [
                  AgentLeaveType.VENDOR_ERROR,
                  AgentLeaveType.NETWORK_DISCONNECTED,
                  AgentLeaveType.AGENT_REPLY_TIMEOUT,
                ].includes(data?.type)
              ) {
                // else if agent disconnect
                if (socketStore.isOutbound()) {
                  socketStore.setStatusToAgentIssue();
                  socketStore.goToComplete();
                } else {
                  this.showAgentPauseModal();
                }
              }
          }
        }
      } catch (e) {
        console.log('on message parse error');
      }
    };

    // when close
    this.ws.onclose = (event: WebSocketMessageEvent) => {
      console.log('ws onclose');
      clearTimeout(this.timer);
      statsStore.clearSendStatsTimerIfSendNow(false);
      this.userConnected = false;
      this.wsConnected = false;
      this.directToCompleteDependOnVideoCallStart();
      socketStore.updateVideoCallStart(false);
      console.log('close', event.data);
    };

    // when error
    this.ws.onerror = (event: WebSocketMessageEvent) => {
      console.log('error', event.data);
    };
  }

  private send(command: ClientCommand, data: Object | null) {
    if (![ClientCommand.PING, ClientCommand.STATISTICS].includes(command)) {
      eventTrack.pushEvent({
        action: command,
        status: {
          type: VMessageType.WEBSOCKET,
        },
        extra: data || undefined,
      });
    }
    const msg = {
      version: VERSION,
      command: command,
      data: data,
    };
    if (!this.ws || !this.wsConnected) return;
    console.log('ws send: ', command, moment().format('HH:mm:ss'));
    try {
      this.ws.send(JSON.stringify(msg));
    } catch (e) {
      console.error(`send ${command} failed`, e);
    }
  }
}

const webSocket = new VWebSocket();
export default webSocket;
